import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { tryGetSupabaseClient } from '#bv/lib/supabaseClient'

export type CustomerTab = 'dashboard' | 'orders' | 'balance' | 'rewards'

const TAB_ICONS: Record<CustomerTab, React.ReactNode> = {
  dashboard: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>,
  orders: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /></svg>,
  balance: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
  rewards: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="7" /><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" /></svg>,
}

export function CustomerChrome({ active, title, subtitle, children }: {
  active: CustomerTab; title: string; subtitle: string; children: React.ReactNode
}) {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [auth, setAuth] = useState<'checking' | 'authed' | 'guest' | 'supabase_missing'>('checking')
  const tabs = useMemo(
    () => [
      { id: 'dashboard' as const, label: 'Dashboard', to: '/customer' as const },
      { id: 'orders' as const, label: 'Orders', to: '/customer/orders' as const },
      { id: 'balance' as const, label: 'Balance', to: '/customer/balance' as const },
      { id: 'rewards' as const, label: 'Rewards', to: '/customer/rewards' as const },
    ] satisfies ReadonlyArray<{ id: CustomerTab; label: string; to: string }>,
    [],
  )

  const [isCompact, setIsCompact] = useState(false)
  const [sessionEmail, setSessionEmail] = useState<string | null>(null)
  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth < 860)
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const supabase = tryGetSupabaseClient()
    if (!supabase) { setSessionEmail(null); setAuth('supabase_missing'); return }
    let cancelled = false
    void (async () => {
      const { data } = await supabase.auth.getSession()
      const em = data.session?.user?.email ?? null
      if (cancelled) return
      setSessionEmail(em)
      setAuth(data.session?.access_token ? 'authed' : 'guest')
    })()
    return () => { cancelled = true }
  }, [pathname])

  const logout = async () => {
    try { const supabase = tryGetSupabaseClient(); await supabase?.auth.signOut() }
    finally { void navigate({ to: '/customer/login', search: { next: '/customer' }, replace: true }) }
  }

  if (auth !== 'authed') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#050508' }}>
        <div className="w-full max-w-md rounded-2xl border p-8 shadow-2xl" style={{ borderColor: 'rgba(0,191,255,0.15)', background: 'linear-gradient(165deg, rgba(14,10,18,0.98), rgba(9,7,12,0.96))', boxShadow: '0 8px 36px rgba(0,0,0,0.45)' }}>
          {auth === 'checking' ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="h-8 w-8 rounded-full border-2 border-[#00bfff] border-t-transparent" style={{ animation: 'spin 0.8s linear infinite' }} />
              <p className="m-0 text-sm text-[#7a92b8]">Checking session...</p>
            </div>
          ) : (
            <>
              <h1 className="m-0 font-display text-2xl tracking-tight chrome-text">Sign in required</h1>
              {auth === 'supabase_missing' ? (
                <p className="m-0 mt-3 text-sm text-red-200">Supabase is not configured. Set <code className="font-mono">VITE_SUPABASE_URL</code> and <code className="font-mono">VITE_SUPABASE_ANON_KEY</code> in your environment.</p>
              ) : (
                <p className="m-0 mt-3 text-sm text-[#7a92b8]">Please log in to view your orders and delivery instructions.</p>
              )}
              <div className="mt-6 flex flex-wrap gap-2">
                <Link to="/customer/login" search={{ next: pathname }} className="btn-primary rounded-xl px-5 py-2.5 text-sm font-semibold">Go to login</Link>
                <Link to="/shop" className="btn-chrome rounded-xl px-5 py-2.5 text-sm font-semibold">Back to shop</Link>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  const emailInitial = sessionEmail ? sessionEmail[0].toUpperCase() : '?'

  return (
    <div className="min-h-screen px-4 py-8 sm:px-6 sm:py-10" style={{ background: '#050508' }}>
      <style dangerouslySetInnerHTML={{ __html: '@keyframes spin{to{transform:rotate(360deg)}}' }} />
      <div className="mx-auto w-full max-w-6xl">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(0, 191, 255,0.2), rgba(139,92,246,0.1))', border: '1px solid rgba(0, 191, 255,0.25)' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00bfff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
            </div>
            <div className="min-w-0">
              <h1 className="m-0 text-xl font-extrabold tracking-tight text-[#f0f6ff] sm:text-2xl">{title}</h1>
              <p className="m-0 text-xs text-[#5a6d8a]">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {sessionEmail ? (
              <span className="hidden sm:flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-xs text-[#7a92b8]">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00bfff]/20 text-[0.6rem] font-bold text-[#93c5fd]">{emailInitial}</span>
                <span className="max-w-[160px] truncate">{sessionEmail}</span>
              </span>
            ) : null}
            <Link to="/shop" className="btn-chrome rounded-xl px-4 py-2 text-xs font-semibold">Shop</Link>
            <button type="button" className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-2 text-xs font-semibold text-[#5a6d8a] transition-colors hover:text-[#fca5a5] hover:border-red-500/20 hover:bg-red-500/[0.04]" onClick={() => void logout()} title="Sign out">Logout</button>
          </div>
        </header>

        <div className="grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
          {!isCompact ? (
            <aside className="h-fit space-y-3">
              {/* Profile card */}
              <div className="rounded-2xl border border-white/[0.06] p-4" style={{ background: 'rgba(10,8,16,0.8)' }}>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold" style={{ background: 'linear-gradient(135deg, #00bfff, #7b2fbe)', color: '#fff' }}>{emailInitial}</div>
                  <div className="min-w-0">
                    <p className="m-0 text-sm font-semibold text-[#f0f6ff] truncate">{sessionEmail ?? 'Customer'}</p>
                    <p className="m-0 text-[0.65rem] text-[#5a6d8a]">Customer account</p>
                  </div>
                </div>
              </div>

              {/* Nav */}
              <nav className="rounded-2xl border border-white/[0.06] p-2.5 space-y-1" style={{ background: 'rgba(10,8,16,0.8)' }} aria-label="Customer dashboard navigation">
                {tabs.map((t) => (
                  <Link
                    key={t.id}
                    to={t.to}
                    className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200 ${active === t.id ? 'text-white' : 'text-[#5a6d8a] hover:text-[#8ecfff] hover:bg-white/[0.02]'}`}
                    style={active === t.id ? { background: 'rgba(0, 191, 255,0.12)', boxShadow: 'inset 0 0 0 1px rgba(0, 191, 255,0.2)', color: '#d4e8ff' } : undefined}
                  >
                    <span style={{ color: active === t.id ? '#93c5fd' : '#5a6d8a' }}>{TAB_ICONS[t.id]}</span>
                    {t.label}
                  </Link>
                ))}
              </nav>

              {/* Tip */}
              <div className="rounded-2xl border border-white/[0.06] p-4 text-[0.7rem] text-[#5a6d8a]" style={{ background: 'rgba(10,8,16,0.6)' }}>
                <span className="font-semibold text-[#5a6d8a]">Tip:</span> Save your order once, then revisit anytime on this device.
              </div>
            </aside>
          ) : null}

          <main className="min-w-0">{children}</main>
        </div>

        {isCompact ? (
          <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-white/[0.06] p-2" style={{ background: 'rgba(5,5,8,0.95)', backdropFilter: 'blur(16px)' }} aria-label="Customer dashboard tabs">
            <div className="mx-auto grid max-w-[480px] grid-cols-4 gap-1">
              {tabs.map((t) => (
                <Link
                  key={t.id}
                  to={t.to}
                  className={`flex flex-col items-center gap-1 rounded-xl px-2 py-2.5 text-center transition-all duration-200 ${active === t.id ? 'text-white' : 'text-[#5a6d8a]'}`}
                  style={active === t.id ? { background: 'rgba(0, 191, 255,0.15)', boxShadow: 'inset 0 0 0 1px rgba(0, 191, 255,0.2)' } : undefined}
                >
                  <span style={{ color: active === t.id ? '#93c5fd' : undefined }}>{TAB_ICONS[t.id]}</span>
                  <span className="text-[0.6rem] font-semibold uppercase tracking-wide">{t.label}</span>
                </Link>
              ))}
            </div>
          </nav>
        ) : null}
      </div>
    </div>
  )
}
